export enum SOCKET_CONNECTION_STATUS {
  /** Connection is not yet established */
  DISCONNECTED,
  /** Connection was forcefully killed */
  TERMINATED,
  /** Connection is in progress */
  CONNECTING,
  /** Connection is established */
  CONNECTED,
  /** Connection was lost and trying to reconnect */
  RECONNECTING,
  /** Connection is re-established after being lost */
  RECONNECTED,
  /** Failed to reconnect after limited number of attempts */
  CONNECTION_LOST,
}

export type SocketSendPayload = string | ArrayBufferLike | Blob | ArrayBufferView;

export type WebSocketEventName = 'open' | 'message' | 'close' | 'error';

export enum SOCKET_CLOSE_REASON {
  KILL = 'kill',
  RECONNECT = 'reconnect',
  OTHER = 'other',
}

export interface Socket {
  readonly url: string | URL;
  readonly readyState: number;

  send(data: SocketSendPayload): void;
  close(): void;

  addEventListener<ev extends WebSocketEventName>(type: ev, listener: (event: WebSocketEventMap[ev]) => void): void;

  removeEventListener<ev extends WebSocketEventName>(type: ev, listener: (event: WebSocketEventMap[ev]) => void): void;
}

export interface SocketConstructor<T extends Socket> {
  new (url: string | URL, protocols?: string | string[]): T;
  readonly CONNECTING: number;
  readonly OPEN: number;
  readonly CLOSING: number;
  readonly CLOSED: number;
}

export abstract class SocketCommunicationStrategy<ParsedMessageType> {
  /**
   * Defines parsing of incoming messages to a single usable format compliant with generic type `MessageType`.
   */
  abstract parseMessage(message: MessageEvent): ParsedMessageType;

  /**
   * Recognizes wether or not the given message is a `pong` type of message according to the particular message format.
   */
  abstract recognizePongMessage: (message: ParsedMessageType) => boolean;
  /**
   * Assembles a ping message compliant with argument of wsocket `send` method.
   */
  abstract assemblePingMessage: () => string | ArrayBufferLike | Blob | ArrayBufferView;
}

export interface WebSockcetServiceOptions {
  /** Timing configuration for mechanism that is trying to keep websocket connection alive.
   * If not provided, keep-alive mechanism will not be used.
   */
  keepAlive?: {
    /** Time in ms to wait before sending first ping and verifying if consequent pong responses have been delivered.
     * If pong is not received within this time, reconnection will be attempted.
     */
    connectionValidationInterval: number;
    /** Time in ms to wait before starting initial reconnection attempt and retry consequent connections in case prvious ones failed */
    reconnectionInterval: number;
    /** Maximum numver of reconnection attempts that can be made in a single cycle */
    reconnectLimit: number;
  };
  useProxyConnection?: boolean;
}

export type WebSocketBaseEvents<ParsedMessageType> = {
  open: (event: Event) => void;
  close: (event: CloseEvent) => void;
  error: (error: Event) => void;
  rawMessage: (event: MessageEvent) => void;
  processedMessage: (message: ParsedMessageType) => void;
  connectionStatusChange: (status: SOCKET_CONNECTION_STATUS) => void;
};
